import React, { useEffect, useState, useCallback } from "react";
import "./index.css";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "./axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { getServiceTitle } from "./data/serviceOptions";
import FormPageHeader from "./components/forms/FormPageHeader";
import ServiceContextBanners from "./components/forms/ServiceContextBanners";
import PersonalInfoSection from "./components/forms/PersonalInfoSection";
import FamilyMedicalHistorySection from "./components/forms/FamilyMedicalHistorySection";
import PersonalMedicalHistorySection from "./components/forms/PersonalMedicalHistorySection";
import ClinicalInputCard from "./components/forms/ClinicalInputCard";
import UrinalysisSection from "./components/forms/UrinalysisSection";
import SmokerStatusSection from "./components/forms/SmokerStatusSection";
import FormFooterSection from "./components/forms/FormFooterSection";

const upsertRecordById = (records, updatedRecord) => {
  const nextRecords = [...records];
  const recordIndex = nextRecords.findIndex(
    (record) => record.id === updatedRecord.id,
  );

  if (recordIndex > -1) {
    nextRecords[recordIndex] = {
      ...nextRecords[recordIndex],
      ...updatedRecord,
    };
  } else {
    nextRecords.push(updatedRecord);
  }

  return nextRecords;
};

const getBloodPressureRag = (value) => {
  const readings = String(value || "")
    .match(/\d+(?:\.\d+)?/g)
    ?.map(Number)
    .filter((reading) => Number.isFinite(reading));

  if (!readings || readings.length < 2) return null;

  const [systolic, diastolic] = readings;

  if (systolic < 90 && diastolic < 60) {
    return {
      status: "blue",
      label: "Blue",
      description: "Low blood pressure: below 90/60",
    };
  }

  if (systolic >= 160 || diastolic >= 100) {
    return {
      status: "red",
      label: "Red",
      description: "High blood pressure: 160/100 and above",
    };
  }

  if (systolic >= 140 || diastolic >= 90) {
    return {
      status: "amber",
      label: "Amber",
      description: "Elevated blood pressure: 140/90 - 159/99",
    };
  }

  return {
    status: "green",
    label: "Green",
    description: "Healthy blood pressure: up to 139/89",
  };
};

const getCholesterolRag = (value) => {
  const cholesterolValue = parseFloat(String(value || "").trim());

  if (!Number.isFinite(cholesterolValue)) return null;

  if (cholesterolValue >= 6.5) {
    return {
      status: "red",
      label: "Red",
      description: "High cholesterol: 6.5 mmol/L and above",
    };
  }

  if (cholesterolValue >= 5) {
    return {
      status: "amber",
      label: "Amber",
      description: "Raised cholesterol: 5.0 - 6.4 mmol/L",
    };
  }

  if (cholesterolValue >= 3.8) {
    return {
      status: "green",
      label: "Green",
      description: "Healthy cholesterol: 3.8 - 4.9 mmol/L",
    };
  }

  return {
    status: "blue",
    label: "Blue",
    description: "Below range: under 3.8 mmol/L",
  };
};

const App = ({ forcedService = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedService =
    location.state?.record?.services ||
    forcedService ||
    location.state?.service ||
    "";
  const serviceTitle = getServiceTitle(selectedService);
  const isBloodAndCholesterolService =
    selectedService === "blood and cholesterol checks";
  const isNurseChatService = selectedService === "nurse chat";
  const [editSession, setEditSession] = useState(
    location.state?.record
      ? {
          source: location.state?.source || "dashboard",
          record: location.state.record,
          returnTo: location.state?.returnTo || "/",
        }
      : null,
  );
  const [dob, setDob] = useState("");
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [weightStone, setWeightStone] = useState("");
  const [weightPounds, setWeightPounds] = useState("");
  const [waistCircumferenceCm, setWaistCircumferenceCm] = useState("");
  const [bmi, setBmi] = useState("");
  const [bmiCategory, setBmiCategory] = useState("");
  const [waistCategory, setWaistCategory] = useState("");
  const [heightUnit, setHeightUnit] = useState("cm");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [bloodPressure, setBloodPressure] = useState("");
  const [pulse, setPulse] = useState("");
  const [blood, setBlood] = useState("");
  const [glucose, setGlucose] = useState("");
  const [additionalResults, setAdditionalResults] = useState("");
  const [cholesterol, setCholesterol] = useState("");
  const [weeklyUnitIntake, setWeeklyUnitIntake] = useState("");
  const [weeklyHours, setWeeklyHours] = useState("");
  const [smokerStatus, setSmokerStatus] = useState("");
  const [services, setServices] = useState("");
  const [routineCancerChecks, setRoutineCancerChecks] = useState([]);
  const [recommendedServices, setRecommendedServices] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [familyMedicalHistory, setFamilyMedicalHistory] = useState([]);
  const [isActiveTreatment, setIsActiveTreatment] = useState(false);
  const [activeTreatmentNotes, setActiveTreatmentNotes] = useState("");
  const [email, setEmail] = useState("");
  const [optOut, setOptOut] = useState(false);
  const [assessingNurse, setAssessingNurse] = useState(
    location.state?.assessingNurse || "",
  );
  const [selectedSchool, setSelectedSchool] = useState(
    location.state?.selectedSchool || null,
  );
  const [isBmiSectionNotCarriedOut, setIsBmiSectionNotCarriedOut] =
    useState(false);

  const isNotCarriedOutValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase() === "n/a";

  const handleNotCarriedOutToggle = (checked, setter) => {
    setter(checked ? "n/a" : "");
  };

  const parseNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsedValue = parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const normalizeWhole = (value, maxValue = null) => {
    const parsedValue = parseNumber(value);
    if (parsedValue === null) return "";

    const roundedValue = Math.round(parsedValue);
    const boundedValue =
      maxValue === null
        ? Math.max(0, roundedValue)
        : Math.min(maxValue, Math.max(0, roundedValue));

    return String(boundedValue);
  };

  const getHeightInCm = useCallback(() => {
    if (heightUnit === "cm") {
      const parsedCm = parseNumber(heightCm);
      return parsedCm && parsedCm > 0 ? parsedCm : null;
    }

    const parsedFeet = Math.max(0, parseNumber(heightFeet) || 0);
    const parsedInches = Math.min(
      11,
      Math.max(0, parseNumber(heightInches) || 0),
    );
    const totalInches = parsedFeet * 12 + parsedInches;

    return totalInches > 0 ? totalInches * 2.54 : null;
  }, [heightCm, heightFeet, heightInches, heightUnit]);

  const getWeightInKg = useCallback(() => {
    if (weightUnit === "kg") {
      const parsedKg = parseNumber(weightKg);
      return parsedKg && parsedKg > 0 ? parsedKg : null;
    }

    const parsedStone = Math.max(0, parseNumber(weightStone) || 0);
    const parsedPounds = Math.min(
      13,
      Math.max(0, parseNumber(weightPounds) || 0),
    );
    const totalPounds = parsedStone * 14 + parsedPounds;

    return totalPounds > 0 ? totalPounds * 0.45359237 : null;
  }, [weightKg, weightStone, weightPounds, weightUnit]);

  const calculateBmi = useCallback(() => {
    if (isBmiSectionNotCarriedOut) {
      setBmi("n/a");
      setBmiCategory("n/a");
      return;
    }

    const calculatedHeightCm = getHeightInCm();
    const calculatedWeightKg = getWeightInKg();

    if (!calculatedHeightCm || !calculatedWeightKg) {
      setBmi("");
      setBmiCategory("");
      return;
    }

    const heightInMeters = calculatedHeightCm / 100;
    const calculatedBmi =
      calculatedWeightKg / (heightInMeters * heightInMeters);
    setBmi(calculatedBmi.toFixed(1));
    if (calculatedBmi < 18.5) {
      setBmiCategory("underweight");
    } else if (calculatedBmi >= 18.5 && calculatedBmi <= 24.9) {
      setBmiCategory("healthy");
    } else if (calculatedBmi >= 25 && calculatedBmi <= 29.9) {
      setBmiCategory("overweight");
    } else if (calculatedBmi >= 30 && calculatedBmi <= 34.9) {
      setBmiCategory("obese");
    } else {
      setBmiCategory("veryObese");
    }
  }, [getHeightInCm, getWeightInKg, isBmiSectionNotCarriedOut]);

  const getWaistCategoryFromCm = useCallback(() => {
    const parsedWaistCm = parseNumber(waistCircumferenceCm);
    if (!parsedWaistCm || parsedWaistCm <= 0) return "";

    const isFemale = (gender || "").toLowerCase() === "female";

    if (isFemale) {
      if (parsedWaistCm < 80) return "<94cm/<80cm";
      if (parsedWaistCm <= 88) return "94-102cm/80-88cm";
      return ">102cm/>88cm";
    }

    if (parsedWaistCm < 94) return "<94cm/<80cm";
    if (parsedWaistCm <= 102) return "94-102cm/80-88cm";
    return ">102cm/>88cm";
  }, [gender, waistCircumferenceCm]);

  const resetForm = () => {
    setEditSession(null);
    setDob("");
    setName("");
    setAge("");
    setGender("");
    setHeightCm("");
    setHeightFeet("");
    setHeightInches("");
    setWeightKg("");
    setWeightStone("");
    setWeightPounds("");
    setWaistCircumferenceCm("");
    setBmi("");
    setBmiCategory("");
    setWaistCategory("");
    setHeightUnit("cm");
    setWeightUnit("kg");
    setBloodPressure("");
    setPulse("");
    setBlood("");
    setGlucose("");
    setAdditionalResults("");
    setCholesterol("");
    setWeeklyUnitIntake("");
    setWeeklyHours("");
    setSmokerStatus("");
    setServices("");
    setRoutineCancerChecks([]);
    setRecommendedServices([]);
    setAdditionalNotes("");
    setFamilyMedicalHistory([]);
    setIsActiveTreatment(false);
    setActiveTreatmentNotes("");
    setEmail("");
    setOptOut(false);
    setAssessingNurse("");
    setSelectedSchool(null);
    setIsBmiSectionNotCarriedOut(false);
  };

  const returnToSelection = ({ skipUnsavedChangesPrompt = false } = {}) => {
    const hasUnsavedChanges = Boolean(
      name ||
      dob ||
      age ||
      gender ||
      email ||
      heightCm ||
      heightFeet ||
      heightInches ||
      weightKg ||
      weightStone ||
      weightPounds ||
      waistCircumferenceCm ||
      bloodPressure ||
      pulse ||
      blood ||
      glucose ||
      additionalResults ||
      cholesterol ||
      weeklyUnitIntake ||
      weeklyHours ||
      smokerStatus ||
      routineCancerChecks.length > 0 ||
      recommendedServices.length > 0 ||
      additionalNotes ||
      familyMedicalHistory.length > 0 ||
      isActiveTreatment ||
      activeTreatmentNotes ||
      optOut,
    );

    if (!skipUnsavedChangesPrompt && hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        "You have unsaved changes. Leave this form and lose your updates?",
      );

      if (!shouldLeave) return;
    }

    const returnPath = editSession?.returnTo || "/";

    resetForm();
    navigate(returnPath, { replace: true });
  };

  useEffect(() => {
    const record = location.state?.record;

    if (!record) return;

    setEditSession({
      source: location.state?.source || "dashboard",
      record,
      returnTo: location.state?.returnTo || "/",
    });
    setServices(record.services || "");
    setDob(record.dob || "");
    setName(record.name || "");
    setAge(record.age || "");
    setGender(record.gender || "");
    setHeightUnit(record.heightUnit || "cm");
    setWeightUnit(record.weightUnit || "kg");
    setBloodPressure(record.bloodPressure || "");
    setPulse(record.pulse || "");
    setBlood(record.blood || "");
    setGlucose(record.glucose || "");
    setAdditionalResults(record.additionalResults || "");
    setCholesterol(record.cholesterol || "");
    setWeeklyUnitIntake(record.weeklyUnitIntake || "");
    setWeeklyHours(record.weeklyHours || "");
    setSmokerStatus(record.smokerStatus || "");
    setRoutineCancerChecks(
      Array.isArray(record.routineCancerChecks)
        ? record.routineCancerChecks
        : typeof record.routineCancerChecks === "string" &&
            record.routineCancerChecks.length > 0
          ? record.routineCancerChecks
              .split(",")
              .map((check) => check.trim())
              .filter(Boolean)
          : [],
    );
    setRecommendedServices(
      Array.isArray(record.recommendedServices)
        ? record.recommendedServices
        : typeof record.recommendedServices === "string" &&
            record.recommendedServices.length > 0
          ? record.recommendedServices
              .split(",")
              .map((service) => service.trim())
              .filter(Boolean)
          : [],
    );
    setAdditionalNotes(record.additionalNotes || "");
    setFamilyMedicalHistory(
      Array.isArray(record.familyMedicalHistory)
        ? record.familyMedicalHistory
        : typeof record.familyMedicalHistory === "string" &&
            record.familyMedicalHistory.length > 0
          ? record.familyMedicalHistory
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean)
          : [],
    );
    setEmail(record.email || "");
    setIsActiveTreatment(Boolean(record.isActiveTreatment));
    setActiveTreatmentNotes(record.activeTreatmentNotes || "");
    setOptOut(Boolean(record.optOut));
    setAssessingNurse(
      record.assessingNurse || location.state?.assessingNurse || "",
    );
    setSelectedSchool(
      record.selectedSchool || location.state?.selectedSchool || null,
    );
    setBmi(record.bmi || "");
    setBmiCategory(record.bmiCategory || "");
    setWaistCategory(record.waistCategory || "");
    setWaistCircumferenceCm(record.waistCircumferenceCm || "");

    const recordHeight = record.height || "";
    const recordWeight = record.weight || "";
    const recordWaist = record.waistCircumferenceCm || "";
    const bmiSectionNotCarriedOut =
      isNotCarriedOutValue(recordHeight) &&
      isNotCarriedOutValue(recordWeight) &&
      isNotCarriedOutValue(recordWaist);
    setIsBmiSectionNotCarriedOut(bmiSectionNotCarriedOut);

    if (isNotCarriedOutValue(recordHeight)) {
      setHeightUnit("cm");
      setHeightCm("n/a");
      setHeightFeet("");
      setHeightInches("");
    } else if ((record.heightUnit || "cm") === "cm") {
      setHeightCm(record.height || "");
      setHeightFeet("");
      setHeightInches("");
    } else {
      const parsedHeight = parseNumber(record.height) || 0;
      const totalInches = parsedHeight * 12;
      let feetValue = Math.floor(totalInches / 12);
      let inchesValue = Math.round(totalInches - feetValue * 12);

      if (inchesValue === 12) {
        feetValue += 1;
        inchesValue = 0;
      }

      setHeightCm("");
      setHeightFeet(String(feetValue));
      setHeightInches(String(inchesValue));
    }

    if (isNotCarriedOutValue(recordWeight)) {
      setWeightUnit("kg");
      setWeightKg("n/a");
      setWeightStone("");
      setWeightPounds("");
    } else if ((record.weightUnit || "kg") === "kg") {
      setWeightKg(record.weight || "");
      setWeightStone("");
      setWeightPounds("");
    } else {
      const parsedWeight = parseNumber(record.weight) || 0;
      const totalPounds = parsedWeight * 14;
      let stoneValue = Math.floor(totalPounds / 14);
      let poundsValue = Math.round(totalPounds - stoneValue * 14);

      if (poundsValue === 14) {
        stoneValue += 1;
        poundsValue = 0;
      }

      setWeightKg("");
      setWeightStone(String(stoneValue));
      setWeightPounds(String(poundsValue));
    }

    if (bmiSectionNotCarriedOut) {
      setBmi("n/a");
      setBmiCategory("n/a");
      setWaistCategory("n/a");
      setWaistCircumferenceCm("n/a");
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedService && !editSession) {
      setServices(selectedService);
      setAssessingNurse(location.state?.assessingNurse || "");
      setSelectedSchool(location.state?.selectedSchool || null);
    }
  }, [editSession, selectedService, location.state]);

  useEffect(() => {
    if (!selectedService && !editSession) {
      navigate("/", { replace: true });
    }
  }, [editSession, navigate, selectedService]);

  const saveFormData = async (formData) => {
    // const isOnline = () => {
    //   return navigator.onLine
    // }

    // if (isOnline()) {
    //   try {
    //     await axios.post('health-screen-report/create', formData)

    //     Swal.fire({
    //       title: 'Health Screen Complete!',
    //       text: 'Your health screen results have been submitted, you will receive an email with a copy of your results.',
    //       icon: 'success',
    //     }).then(() => {
    //       window.location.reload()
    //     })
    //   } catch (error) {
    //     Swal.fire({
    //       title: 'Error Occured!',
    //       text: 'There was an error submitting your results, please try again',
    //       icon: 'error',
    //     })
    //     console.error('Error saving data:', error)
    //   }
    // } else {
    await saveDataLocally(formData);
    //   console.log('Data saved locally')
    // }
  };

  const saveDataLocally = async (formData) => {
    try {
      const record = {
        ...formData,
        id: crypto.randomUUID ? crypto.randomUUID() : `form_${Date.now()}`,
        status: "pending",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        submittedAt: null,
        backendId: null,
        errorMessage: null,
      };

      const existingQueue = JSON.parse(
        localStorage.getItem("healthScreenData") || "[]",
      );
      existingQueue.push(record);
      localStorage.setItem("healthScreenData", JSON.stringify(existingQueue));

      const existingArchive = JSON.parse(
        localStorage.getItem("allHealthScreenData") || "[]",
      );
      existingArchive.push(record);
      localStorage.setItem(
        "allHealthScreenData",
        JSON.stringify(existingArchive),
      );

      Swal.fire({
        title: "Health Screen Complete!",
        text: "Your health screen results have been saved locally. An admin will submit them later.",
        icon: "success",
      }).then(() => {
        returnToSelection({ skipUnsavedChangesPrompt: true });
      });
    } catch (error) {
      console.error("Error saving data locally:", error);
    }
  };

  const saveEditedForm = async (formData) => {
    if (!editSession?.record) return;

    try {
      const editedRecord = {
        ...editSession.record,
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      if (
        editSession.source === "admin" &&
        editSession.record.status === "submitted"
      ) {
        const response = await axios.post(
          "health-screen-report/create",
          formData,
        );
        const archivedRecords = upsertRecordById(
          JSON.parse(localStorage.getItem("allHealthScreenData") || "[]"),
          {
            ...editedRecord,
            status: "submitted",
            submittedAt: new Date().toISOString(),
            backendId: response?.data?.id ?? editedRecord.backendId ?? null,
            errorMessage: null,
          },
        );
        localStorage.setItem(
          "allHealthScreenData",
          JSON.stringify(archivedRecords),
        );
        const remainingQueue = JSON.parse(
          localStorage.getItem("healthScreenData") || "[]",
        ).filter((record) => record.id !== editedRecord.id);
        localStorage.setItem(
          "healthScreenData",
          JSON.stringify(remainingQueue),
        );

        Swal.fire({
          title: "Resubmitted",
          text: `${editedRecord.name}'s form has been updated and resent.`,
          icon: "success",
        }).then(() => {
          returnToSelection({ skipUnsavedChangesPrompt: true });
        });

        return;
      }

      const status =
        editSession.source === "admin" &&
        editSession.record.status === "submitted"
          ? "submitted"
          : "pending";
      const queuedRecords = upsertRecordById(
        JSON.parse(localStorage.getItem("healthScreenData") || "[]"),
        { ...editedRecord, status },
      );
      localStorage.setItem("healthScreenData", JSON.stringify(queuedRecords));

      const archivedRecords = upsertRecordById(
        JSON.parse(localStorage.getItem("allHealthScreenData") || "[]"),
        { ...editedRecord, status },
      );
      localStorage.setItem(
        "allHealthScreenData",
        JSON.stringify(archivedRecords),
      );

      Swal.fire({
        title: "Updated",
        text: `${editedRecord.name}'s form has been updated.`,
        icon: "success",
      }).then(() => {
        returnToSelection({ skipUnsavedChangesPrompt: true });
      });
    } catch (error) {
      console.error("Error saving edited form:", error);
      Swal.fire("Error", "There was an error saving the edited form.", "error");
    }
  };

  // const syncData = useCallback(async () => {
  //   try {
  //     const isOnline = () => {
  //       return navigator.onLine
  //     }
  //     // Retrieve the locally saved data
  //     const savedData = localStorage.getItem('healthScreenData')
  //     if (savedData && isOnline()) {
  //       const formData = JSON.parse(savedData)
  //       formData.forEach(async (data) => {
  //         console.log(data)
  //         // Submit the saved data when online
  //         await axios.post('health-screen-report/create', data)
  //       })

  //       localStorage.removeItem('healthScreenData')
  //     }
  //   } catch (error) {
  //     console.error('Error syncing data:', error)
  //   }
  // }, [])

  // useEffect(() => {
  //   syncData()
  // }, [syncData])

  useEffect(() => {
    calculateBmi();
  }, [calculateBmi]);

  useEffect(() => {
    if (isBmiSectionNotCarriedOut) {
      setWaistCategory("n/a");
      return;
    }

    const mappedWaistCategory = getWaistCategoryFromCm();
    setWaistCategory(mappedWaistCategory);
  }, [getWaistCategoryFromCm, isBmiSectionNotCarriedOut]);

  const handleClick = (bmiCategory, waistCategory) => {
    if (isBmiSectionNotCarriedOut) return;

    setBmiCategory(bmiCategory);
    setWaistCategory(waistCategory);
  };

  const toggleHeightUnit = (targetUnit) => {
    if (targetUnit === heightUnit) return;

    const currentHeightCm = getHeightInCm();

    if (targetUnit === "cm") {
      setHeightUnit("cm");
      setHeightCm(currentHeightCm ? String(Math.round(currentHeightCm)) : "");
      return;
    }

    setHeightUnit("feet");

    if (!currentHeightCm) {
      setHeightFeet("");
      setHeightInches("");
      return;
    }

    const totalInches = currentHeightCm / 2.54;
    let feetValue = Math.floor(totalInches / 12);
    let inchesValue = Math.round(totalInches - feetValue * 12);

    if (inchesValue === 12) {
      feetValue += 1;
      inchesValue = 0;
    }

    setHeightFeet(String(feetValue));
    setHeightInches(String(inchesValue));
  };

  const toggleWeightUnit = (targetUnit) => {
    if (targetUnit === weightUnit) return;

    const currentWeightKg = getWeightInKg();

    if (targetUnit === "kg") {
      setWeightUnit("kg");
      setWeightKg(
        currentWeightKg ? String(Number(currentWeightKg.toFixed(1))) : "",
      );
      return;
    }

    setWeightUnit("stone");

    if (!currentWeightKg) {
      setWeightStone("");
      setWeightPounds("");
      return;
    }

    const totalPounds = currentWeightKg / 0.45359237;
    let stoneValue = Math.floor(totalPounds / 14);
    let poundsValue = Math.round(totalPounds - stoneValue * 14);

    if (poundsValue === 14) {
      stoneValue += 1;
      poundsValue = 0;
    }

    setWeightStone(String(stoneValue));
    setWeightPounds(String(poundsValue));
  };

  const handleDobChange = (e) => {
    setDob(e.target.value);
    const birthDate = new Date(e.target.value);
    const currentDate = new Date();
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const monthDifference = currentDate.getMonth() - birthDate.getMonth();
    const dayDifference = currentDate.getDate() - birthDate.getDate();

    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    setAge(age);
  };

  const hasValidBodyMetrics =
    !isBmiSectionNotCarriedOut && getHeightInCm() && getWeightInKg();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !dob || !email) {
      toast.error("Please fill in all the required fields.");
      return;
    }

    const normalizedHeightCm = getHeightInCm();
    const normalizedWeightKg = getWeightInKg();
    const normalizedWaistCm = parseNumber(waistCircumferenceCm);
    const isHeightNotCarriedOut =
      isBmiSectionNotCarriedOut || isNotCarriedOutValue(heightCm);
    const isWeightNotCarriedOut =
      isBmiSectionNotCarriedOut || isNotCarriedOutValue(weightKg);
    const isWaistNotCarriedOut =
      isBmiSectionNotCarriedOut || isNotCarriedOutValue(waistCircumferenceCm);

    const formHeight = isHeightNotCarriedOut
      ? "n/a"
      : normalizedHeightCm
        ? heightUnit === "cm"
          ? String(Number(normalizedHeightCm.toFixed(1)))
          : String(Number((normalizedHeightCm / 30.48).toFixed(2)))
        : "";

    const formWeight = isWeightNotCarriedOut
      ? "n/a"
      : normalizedWeightKg
        ? weightUnit === "kg"
          ? String(Number(normalizedWeightKg.toFixed(1)))
          : String(Number((normalizedWeightKg / 6.35029318).toFixed(2)))
        : "";

    const formData = {
      name,
      gender,
      dob,
      age,
      height: formHeight,
      weight: formWeight,
      heightUnit,
      weightUnit,
      bmi: isBmiSectionNotCarriedOut ? "n/a" : bmi,
      bmiCategory: isBmiSectionNotCarriedOut ? "n/a" : bmiCategory,
      waistCircumferenceCm: isWaistNotCarriedOut
        ? "n/a"
        : normalizedWaistCm
          ? String(Number(normalizedWaistCm.toFixed(1)))
          : "",
      waistCategory: isBmiSectionNotCarriedOut ? "n/a" : waistCategory,
      bloodPressure,
      pulse,
      blood,
      glucose,
      additionalResults,
      cholesterol,
      weeklyUnitIntake,
      weeklyHours,
      smokerStatus,
      services,
      routineCancerChecks,
      recommendedServices,
      additionalNotes,
      familyMedicalHistory,
      isActiveTreatment,
      activeTreatmentNotes,
      email,
      optOut,
      assessingNurse,
      selectedSchool,
    };

    if (editSession?.record) {
      await saveEditedForm(formData);
      return;
    }

    await saveFormData(formData);
  };

  const handleRecommendedServicesChange = (selection) => {
    if (Array.isArray(selection)) {
      setRecommendedServices(selection);
      return;
    }

    const selectedValues = Array.from(
      selection.target.selectedOptions,
      (option) => option.value,
    );
    setRecommendedServices(selectedValues);
  };

  const handleRoutineCancerChecksChange = (selection) => {
    setRoutineCancerChecks(Array.isArray(selection) ? selection : []);
  };

  const handleBmiSectionToggle = (checked) => {
    setIsBmiSectionNotCarriedOut(checked);

    if (checked) {
      setHeightUnit("cm");
      setWeightUnit("kg");
      setHeightCm("n/a");
      setHeightFeet("");
      setHeightInches("");
      setWeightKg("n/a");
      setWeightStone("");
      setWeightPounds("");
      setWaistCircumferenceCm("n/a");
      setBmi("n/a");
      setBmiCategory("n/a");
      setWaistCategory("n/a");
      return;
    }

    setHeightCm((previous) => (isNotCarriedOutValue(previous) ? "" : previous));
    setWeightKg((previous) => (isNotCarriedOutValue(previous) ? "" : previous));
    setWaistCircumferenceCm((previous) =>
      isNotCarriedOutValue(previous) ? "" : previous,
    );
    setBmi("");
    setBmiCategory("");
    setWaistCategory("");
  };

  const submitLabel = editSession
    ? editSession.source === "admin" &&
      editSession.record?.status === "submitted"
      ? "Save & Resend"
      : "Save Changes"
    : "Submit";

  const bloodPressureRag = getBloodPressureRag(bloodPressure);
  const cholesterolRag = getCholesterolRag(cholesterol);

  if (isNurseChatService) {
    return (
      <div className="relative">
        <FormPageHeader onLoginClick={() => navigate("/login")} />
        <div className="min-h-screen max-w-4xl mx-auto">
          <ServiceContextBanners
            editSession={editSession}
            serviceTitle={serviceTitle}
            assessingNurse={assessingNurse}
            selectedSchool={selectedSchool}
            onChangeService={returnToSelection}
          />

          <form onSubmit={handleSubmit}>
            <div className="w-full py-6 flex flex-col justify-start relative z-10">
              <PersonalInfoSection
                name={name}
                setName={setName}
                gender={gender}
                setGender={setGender}
                dob={dob}
                handleDobChange={handleDobChange}
                age={age}
                setAge={setAge}
                email={email}
                setEmail={setEmail}
              />

              <div className="px-4 mt-8">
                <FormFooterSection
                  services={services}
                  routineCancerChecks={routineCancerChecks}
                  onRoutineCancerChecksChange={handleRoutineCancerChecksChange}
                  recommendedServices={recommendedServices}
                  onRecommendedServicesChange={handleRecommendedServicesChange}
                  additionalNotes={additionalNotes}
                  onAdditionalNotesChange={(e) =>
                    setAdditionalNotes(e.target.value)
                  }
                  optOut={optOut}
                  onOptOutChange={(e) => setOptOut(e.target.checked)}
                  submitLabel={submitLabel}
                />
              </div>
            </div>
          </form>
          <ToastContainer />
        </div>
      </div>
    );
  }

  if (isBloodAndCholesterolService) {
    return (
      <div className="relative">
        <FormPageHeader onLoginClick={() => navigate("/login")} />
        <div className="min-h-screen max-w-4xl mx-auto">
          <ServiceContextBanners
            editSession={editSession}
            serviceTitle={serviceTitle}
            assessingNurse={assessingNurse}
            selectedSchool={selectedSchool}
            onChangeService={returnToSelection}
          />

          <div className="mx-4 mb-6 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-slate-700 shadow-sm">
            Enter personal details, then capture blood pressure, pulse and
            cholesterol.
          </div>

          <form onSubmit={handleSubmit}>
            <div className="w-full py-6 flex flex-col justify-start relative z-10">
              <PersonalInfoSection
                name={name}
                setName={setName}
                gender={gender}
                setGender={setGender}
                dob={dob}
                handleDobChange={handleDobChange}
                age={age}
                setAge={setAge}
                email={email}
                setEmail={setEmail}
              />
              <div className="px-4 mt-4 space-y-4">
                <ClinicalInputCard
                  title="Blood Pressure"
                  description="If there is too much pressure in your blood vessels, it puts extra strain on your arteries and heart, which can lead to serious health conditions such as heart attack, heart failure, kidney disease, stroke, or dementia."
                  value={bloodPressure}
                  onChange={(e) => setBloodPressure(e.target.value)}
                  required
                  placeholder="120/80"
                  link="https://bloodpressureuk.org"
                  linkLabel="www.bloodpressureuk.org"
                  status={bloodPressureRag?.status}
                  statusLabel={bloodPressureRag?.label}
                  statusDescription={bloodPressureRag?.description}
                />
                <ClinicalInputCard
                  title="Pulse"
                  description="A pulse reading checks the rhythm of your heart and can assist in detecting any irregular heart rhythms."
                  value={pulse}
                  onChange={(e) => setPulse(e.target.value)}
                  required
                  link="https://bhf.org.uk/informationsupport/tests/checking-your-pulse"
                  linkLabel="www.bhf.org.uk/informationsupport/tests/checking-your-pulse"
                />
                <ClinicalInputCard
                  title="Cholesterol"
                  description="Cholesterol is a fat that is carried around your body in the blood."
                  value={cholesterol}
                  onChange={(e) => setCholesterol(e.target.value)}
                  required
                  placeholder="4.8"
                  status={cholesterolRag?.status}
                  statusLabel={cholesterolRag?.label}
                  statusDescription={cholesterolRag?.description}
                />
              </div>

              <div className="px-4 mt-8">
                <FormFooterSection
                  services={services}
                  routineCancerChecks={routineCancerChecks}
                  onRoutineCancerChecksChange={handleRoutineCancerChecksChange}
                  recommendedServices={recommendedServices}
                  onRecommendedServicesChange={handleRecommendedServicesChange}
                  additionalNotes={additionalNotes}
                  onAdditionalNotesChange={(e) =>
                    setAdditionalNotes(e.target.value)
                  }
                  optOut={optOut}
                  onOptOutChange={(e) => setOptOut(e.target.checked)}
                  submitLabel={submitLabel}
                />
              </div>
            </div>
          </form>
          <ToastContainer />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <FormPageHeader onLoginClick={() => navigate("/login")} />
      <div className=" min-h-screen max-w-4xl  mx-auto">
        <ServiceContextBanners
          editSession={editSession}
          serviceTitle={serviceTitle}
          assessingNurse={assessingNurse}
          selectedSchool={selectedSchool}
          onChangeService={returnToSelection}
        />
        <form onSubmit={handleSubmit}>
          <div className="w-full py-6 flex flex-col justify-start relative z-10">
            <PersonalInfoSection
              name={name}
              setName={setName}
              gender={gender}
              setGender={setGender}
              dob={dob}
              handleDobChange={handleDobChange}
              age={age}
              setAge={setAge}
              email={email}
              setEmail={setEmail}
            />
            <FamilyMedicalHistorySection
              values={familyMedicalHistory}
              onChange={setFamilyMedicalHistory}
            />
            <PersonalMedicalHistorySection
              isActiveTreatment={isActiveTreatment}
              onActiveTreatmentChange={(e) => {
                const checked = e.target.checked;
                setIsActiveTreatment(checked);
                if (!checked) {
                  setActiveTreatmentNotes("");
                }
              }}
              activeTreatmentNotes={activeTreatmentNotes}
              onActiveTreatmentNotesChange={(e) =>
                setActiveTreatmentNotes(e.target.value)
              }
            />
            <div className="px-4 mt-4">
              <div className="rounded-xl border border-sas-primary/20 bg-white px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-fatfrank text-base text-sas-primary">
                    BMI Measurements
                  </p>
                  <p className="text-xs text-sas-primary-dark font-montserrat">
                    Includes height, weight, waist circumference and BMI.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sas-primary">
                    Not carried out
                  </span>
                  <span className="relative inline-flex h-6 w-11 items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={isBmiSectionNotCarriedOut}
                      onChange={(e) => handleBmiSectionToggle(e.target.checked)}
                    />
                    <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-sas-primary"></span>
                    <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition peer-checked:translate-x-5"></span>
                  </span>
                </label>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 md:mt-4 px-4">
              <div>
                <label
                  className="block font-fatfrank text-sas-primary md:text-lg font-bold"
                  htmlFor="height"
                >
                  Height
                </label>
                <div className="pb-4">
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${
                      heightUnit === "cm" ? "bg-sas-primary" : "bg-gray-400"
                    } mr-2 text-white font-fatfrank text-sm`}
                    onClick={() => toggleHeightUnit("cm")}
                    disabled={isBmiSectionNotCarriedOut}
                  >
                    Metric
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${
                      heightUnit === "feet" ? "bg-sas-primary" : "bg-gray-400"
                    } text-white font-fatfrank text-sm`}
                    onClick={() => toggleHeightUnit("feet")}
                    disabled={isBmiSectionNotCarriedOut}
                  >
                    Imperial
                  </button>
                </div>
                {heightUnit === "cm" ? (
                  <input
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                    id="height"
                    type="text"
                    name="height"
                    placeholder="163cm"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    disabled={isBmiSectionNotCarriedOut}
                    autoComplete="off"
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-2 ">
                    <input
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                      id="height-feet"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Feet"
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      disabled={isBmiSectionNotCarriedOut}
                      onBlur={() =>
                        setHeightFeet((previousValue) =>
                          normalizeWhole(previousValue),
                        )
                      }
                      autoComplete="off"
                    />
                    <input
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                      id="height-inches"
                      type="number"
                      min="0"
                      max="11"
                      step="1"
                      placeholder="Inches"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      disabled={isBmiSectionNotCarriedOut}
                      onBlur={() =>
                        setHeightInches((previousValue) =>
                          normalizeWhole(previousValue, 11),
                        )
                      }
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
              <div>
                <label
                  className="block font-fatfrank text-sas-primary md:text-lg font-bold"
                  htmlFor="weight"
                >
                  Weight
                </label>
                <div className="pb-4">
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${
                      weightUnit === "kg" ? "bg-sas-primary" : "bg-gray-400"
                    } mr-2 text-white font-fatfrank text-sm`}
                    onClick={() => toggleWeightUnit("kg")}
                    disabled={isBmiSectionNotCarriedOut}
                  >
                    Kilograms
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${
                      weightUnit === "stone" ? "bg-sas-primary" : "bg-gray-400"
                    } text-white font-fatfrank text-sm`}
                    onClick={() => toggleWeightUnit("stone")}
                    disabled={isBmiSectionNotCarriedOut}
                  >
                    Stone
                  </button>
                </div>
                {weightUnit === "kg" ? (
                  <input
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                    id="weight"
                    type="text"
                    name="weight"
                    placeholder="70kg"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    disabled={isBmiSectionNotCarriedOut}
                    autoComplete="off"
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                      id="weight-stone"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Stone"
                      value={weightStone}
                      onChange={(e) => setWeightStone(e.target.value)}
                      disabled={isBmiSectionNotCarriedOut}
                      onBlur={() =>
                        setWeightStone((previousValue) =>
                          normalizeWhole(previousValue),
                        )
                      }
                      autoComplete="off"
                    />
                    <input
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                      id="weight-pounds"
                      type="number"
                      min="0"
                      max="13"
                      step="1"
                      placeholder="Pounds"
                      value={weightPounds}
                      onChange={(e) => setWeightPounds(e.target.value)}
                      disabled={isBmiSectionNotCarriedOut}
                      onBlur={() =>
                        setWeightPounds((previousValue) =>
                          normalizeWhole(previousValue, 13),
                        )
                      }
                      autoComplete="off"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4 px-4">
              <div>
                <label
                  className="block font-fatfrank text-sas-primary md:text-lg font-bold"
                  htmlFor="waist-circumference"
                >
                  Waist Circumference
                </label>
                <input
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white"
                  id="waist-circumference"
                  type="text"
                  placeholder="90cm"
                  value={waistCircumferenceCm}
                  onChange={(e) => setWaistCircumferenceCm(e.target.value)}
                  disabled={isBmiSectionNotCarriedOut}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="px-4">
              <h2 className="mt-4 font-fatfrank text-sas-primary text-lg md:text-2xl">
                Body Mass Index
              </h2>
              <div className="text-sm md:text-base mt-1 rounded-lg p-4 bg-sas-primary-hover text-sas-primary-dark font-montserrat px-4">
                BMI is an indicator of overall weight and body fat which is
                calculated by dividing a person's weight in kilograms by the
                square of their height in metres. Waist circumference
                measurement provides an indicator of abdominal weight. Both
                these measurements can provide relevant information for health
                care professionals that may support an individual's health
                goals.
              </div>
            </div>
            {hasValidBodyMetrics && (
              <div className="my-4 md:mt-10">
                <div className="bg-sas-primary-dark rounded-t-[110px] text-white text-center space-y-4 font-montserrat">
                  <div className="pt-4 md:pt-8 text-sm md:text-lg">
                    Your BMI is{" "}
                  </div>
                  <div className="text-3xl md:text-4xl font-bold font-fatfrank">
                    {bmi || "N/A"}
                  </div>
                  <div className="pb-4 md:pb-10 text-xs md:text-base px-4 md:px-8">
                    {bmi < 18.5 && (
                      <p>
                        A BMI of {bmi} indicates you are underweight for your
                        height and age. Bringing your weight up to a healthy
                        level will help reduce your risk of developing health
                        problems.
                      </p>
                    )}
                    {bmi >= 18.5 && bmi <= 24.9 && (
                      <p>
                        A BMI between 18.5 - 24.9 indicates you are a healthy
                        weight for your height and age. Maintaining a healthy
                        weight will lower your risk of developing weight-related
                        health problems, like type 2 diabetes or heart disease.
                      </p>
                    )}
                    {bmi >= 25 && bmi <= 29.9 && (
                      <p>
                        A BMI between 25 - 29.9 indicates you are overweight for
                        your height and age. Reaching a healthy weight will
                        lower your risk of developing weight-related health
                        problems.
                      </p>
                    )}
                    {bmi >= 30 && bmi <= 34.9 && (
                      <p>
                        A BMI between 30 - 34.9 indicates you are obese for your
                        height and age. Reaching a healthy weight will lower
                        your risk of developing weight-related health problems.
                        Note that individuals with a high muscle mass, such as
                        athletes, may have an elevated BMI due to increased
                        muscle weight. Therefore, BMI may overestimate body fat
                        in these cases.
                      </p>
                    )}
                    {bmi > 34.9 && (
                      <p>
                        A BMI greater than 34.9 indicates you are very obese for
                        your height and age. Reaching a healthy weight will
                        significantly lower your risk of developing
                        weight-related health problems.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div
              className={`relative overflow-hidden mt-8 font-montserrat pb-4 px-4 ${
                isBmiSectionNotCarriedOut
                  ? "opacity-50 pointer-events-none"
                  : ""
              }`}
            >
              {/* This is the half-circle shape */}
              <div className="absolute bottom-0 right-0 w-1/2 h-96 flex items-center justify-center overflow-hidden z-0">
                <div className="w-full h-full bg-sas-primary rounded-tl-full"></div>
              </div>
              <div className="relative grid grid-cols-4 gap-1 md:gap-2 z-10">
                {/* BMI Header */}
                <div className="col-span-1 bg-blue-500 text-white text-center p-2 md:p-4 rounded-lg flex items-center justify-center">
                  <h2 className="font-bold text-xl md:text-3xl font-fatfrank">
                    BMI
                  </h2>
                </div>
                {/* Waist Circumference Header */}
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2">
                  <div className="text-center p-2 md:p-4 flex items-center justify-center col-span-3 font-fatfrank border border-sas-primary-dark rounded-lg bg-white">
                    <h2 className="font-bold text-sm md:text-lg tracking-wide text-sas-primary-dark">
                      Waist Circumference
                    </h2>
                  </div>
                  <div className="text-center bg-blue-200 rounded-lg flex items-center justify-center font-medium text-sas-primary-dark text-xs md:text-base p-2 md:p-4">
                    <p>
                      Men: &lt; 94cm
                      <br />
                      Women: &lt; 80cm
                    </p>
                  </div>
                  <div className="text-center bg-blue-300 rounded-lg flex items-center justify-center font-medium text-sas-primary-dark text-xs md:text-base p-2 md:p-4">
                    <p>
                      Men: 94 - 102cm
                      <br />
                      Women: 80 - 88cm
                    </p>
                  </div>
                  <div className="text-center bg-blue-400 text-white rounded-lg flex items-center justify-center font-medium text-xs md:text-base p-2 md:p-4">
                    <p>
                      Men: &gt; 102cm
                      <br />
                      Women: &gt; 88cm
                    </p>
                  </div>
                </div>
              </div>

              {/* BMI Rows */}
              <div className="grid grid-cols-4 gap-1 md:gap-2 mt-1 md:mt-2 relative z-10">
                <div className="col-span-1 bg-blue-100 rounded-lg flex items-center justify-center text-sas-primary-dark text-xs md:text-base p-2 md:p-4">
                  <p className="font-medium md:font-bold">
                    &lt; 18.5 kg/m2
                    <br />
                    Underweight
                  </p>
                </div>
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2">
                  <div
                    className={`text-center rounded-lg flex items-center justify-center text-xs md:text-base p-2 font-medium md:p-4 cursor-pointer border border-sas-primary-dark ${
                      bmiCategory === "underweight" &&
                      waistCategory === "<94cm/<80cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("underweight", "<94cm/<80cm")}
                  >
                    <p>Underweight</p>
                  </div>
                  <div
                    className={`text-center rounded-lg flex items-center justify-center text-xs md:text-base p-2 font-medium md:p-4 cursor-pointer border border-sas-primary-dark ${
                      bmiCategory === "underweight" &&
                      waistCategory === "94-102cm/80-88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() =>
                      handleClick("underweight", "94-102cm/80-88cm")
                    }
                  >
                    <p>Underweight</p>
                  </div>
                  <div
                    className={`text-center  rounded-lg flex items-center justify-center text-xs md:text-base p-2 font-medium md:p-4 cursor-pointer border border-sas-primary-dark ${
                      bmiCategory === "underweight" &&
                      waistCategory === ">102cm/>88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("underweight", ">102cm/>88cm")}
                  >
                    <p>Underweight</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-1 md:gap-2 mt-1 md:mt-2 relative z-10">
                <div className="col-span-1 bg-blue-200 p-2 md:p-4 rounded-lg flex items-center justify-center text-sas-primary-dark text-xs md:text-base">
                  <p className="font-medium md:font-bold text-center">
                    18.5-24.9 kg/m2
                    <br />
                    Healthy Weight
                  </p>
                </div>
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2 text-sas-primary-dark ">
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base font-medium p-2 md:p-4 border border-sas-primary-dark ${
                      bmiCategory === "healthy" &&
                      waistCategory === "<94cm/<80cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("healthy", "<94cm/<80cm")}
                  >
                    <p>No increased risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base font-medium p-2 md:p-4 border border-sas-primary-dark ${
                      bmiCategory === "healthy" &&
                      waistCategory === "94-102cm/80-88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("healthy", "94-102cm/80-88cm")}
                  >
                    <p>No increased risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base font-medium p-2 md:p-4 border border-sas-primary-dark ${
                      bmiCategory === "healthy" &&
                      waistCategory === ">102cm/>88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("healthy", ">102cm/>88cm")}
                  >
                    <p>Increased risk</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-1 md:gap-2 mt-1 md:mt-2 relative z-10">
                <div className="col-span-1 bg-blue-300 p-2 md:p-4 rounded-lg flex items-center justify-center text-sas-primary-dark text-xs md:text-base">
                  <p className="font-medium md:font-bold text-center">
                    25-29.9 kg/m2
                    <br />
                    Overweight
                  </p>
                </div>
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2 text-sas-primary-dark">
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "overweight" &&
                      waistCategory === "<94cm/<80cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("overweight", "<94cm/<80cm")}
                  >
                    <p>No increased risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "overweight" &&
                      waistCategory === "94-102cm/80-88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() =>
                      handleClick("overweight", "94-102cm/80-88cm")
                    }
                  >
                    <p>Increased risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "overweight" &&
                      waistCategory === ">102cm/>88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("overweight", ">102cm/>88cm")}
                  >
                    <p>High risk</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-1 md:gap-2 mt-1 md:mt-2 relative z-10">
                <div className="col-span-1 bg-blue-400 p-2 md:p-4 rounded-lg text-white flex items-center justify-center text-xs md:text-base">
                  <p className="font-medium md:font-bold text-center">
                    30-34.9 kg/m2
                    <br />
                    Obese
                  </p>
                </div>
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2 text-sas-primary-dark">
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "obese" && waistCategory === "<94cm/<80cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("obese", "<94cm/<80cm")}
                  >
                    <p>Increased risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "obese" &&
                      waistCategory === "94-102cm/80-88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("obese", "94-102cm/80-88cm")}
                  >
                    <p>High risk</p>
                  </div>
                  <div
                    className={`text-center cursor-pointer rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 font-medium border border-sas-primary-dark ${
                      bmiCategory === "obese" &&
                      waistCategory === ">102cm/>88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("obese", ">102cm/>88cm")}
                  >
                    <p>Very high risk</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-1 md:gap-2 mt-1 md:mt-2 relative z-10">
                <div className="col-span-1 bg-blue-500 rounded-lg text-white flex items-center justify-center text-xs md:text-base p-2 md:p-4">
                  <p className="font-medium md:font-bold text-center">
                    &gt; 34.9 kg/m2
                    <br />
                    Very Obese
                  </p>
                </div>
                <div className="col-span-3 grid grid-cols-3 gap-1 md:gap-2 text-sas-primary-dark">
                  <div
                    className={`text-center rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 cursor-pointer font-medium border border-sas-primary-dark ${
                      bmiCategory === "veryObese" &&
                      waistCategory === "<94cm/<80cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("veryObese", "<94cm/<80cm")}
                  >
                    <p>Very high risk</p>
                  </div>
                  <div
                    className={`text-center rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 cursor-pointer font-medium border border-sas-primary-dark ${
                      bmiCategory === "veryObese" &&
                      waistCategory === "94-102cm/80-88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("veryObese", "94-102cm/80-88cm")}
                  >
                    <p>Very high risk</p>
                  </div>
                  <div
                    className={`text-center rounded-lg flex items-center justify-center text-xs md:text-base p-2 md:p-4 cursor-pointer font-medium border border-sas-primary-dark ${
                      bmiCategory === "veryObese" &&
                      waistCategory === ">102cm/>88cm"
                        ? "bg-sas-primary-dark text-white"
                        : "bg-white hover:bg-sas-primary-hover"
                    }`}
                    onClick={() => handleClick("veryObese", ">102cm/>88cm")}
                  >
                    <p>Very high risk</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 mt-6 space-y-4">
              <ClinicalInputCard
                title="Blood Pressure"
                description="If there is too much pressure in your blood vessels, it puts extra strain on your arteries and heart, which can lead to serious health conditions such as heart attack, heart failure, kidney disease, stroke, or dementia."
                value={bloodPressure}
                onChange={(e) => setBloodPressure(e.target.value)}
                placeholder="120/80"
                link="https://bloodpressureuk.org"
                linkLabel="www.bloodpressureuk.org"
                status={bloodPressureRag?.status}
                statusLabel={bloodPressureRag?.label}
                statusDescription={bloodPressureRag?.description}
              />
              <ClinicalInputCard
                title="Pulse"
                description="A pulse reading checks the rhythm of your heart and can assist in detecting any irregular heart rhythms. If left untreated Atrial Fibrillation can increase the risk of stroke, heart failure and other heart-related problems. Treatment and improving lifestyle is important."
                value={pulse}
                onChange={(e) => setPulse(e.target.value)}
                link="https://bhf.org.uk/informationsupport/tests/checking-your-pulse"
                linkLabel="www.bhf.org.uk/informationsupport/tests/checking-your-pulse"
              />
            </div>

            <UrinalysisSection
              blood={blood}
              onBloodChange={(e) => setBlood(e.target.value)}
              glucose={glucose}
              onGlucoseChange={(e) => setGlucose(e.target.value)}
              additionalResults={additionalResults}
              onAdditionalResultsChange={(e) =>
                setAdditionalResults(e.target.value)
              }
            />
            <div className="overflow-hidden px-4 relative z-10">
              <div className="mt-8">
                <ClinicalInputCard
                  title="Cholesterol"
                  description="Cholesterol is a fat that is carried around your body in the blood. It is vital that you have enough of it, for your body to work properly. Too much cholesterol can cause your arteries to become blocked, increasing your risk of heart disease and stroke."
                  value={cholesterol}
                  onChange={(e) => setCholesterol(e.target.value)}
                  placeholder="4.8"
                  link="https://heartuk.org.uk/cholesterol/overview"
                  linkLabel="www.heartuk.org.uk/cholesterol/overview"
                  status={cholesterolRag?.status}
                  statusLabel={cholesterolRag?.label}
                  statusDescription={cholesterolRag?.description}
                />
              </div>

              <div className="relative my-8">
                <div className="rounded-2xl border border-sky-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-sky-100 bg-sky-50 flex items-center justify-between gap-3">
                    <h2 className="font-fatfrank text-lg md:text-xl text-sas-primary">
                      Alcohol
                    </h2>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sas-primary">
                        Not carried out
                      </span>
                      <span className="relative inline-flex h-5 w-10 items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={isNotCarriedOutValue(weeklyUnitIntake)}
                          onChange={(e) =>
                            handleNotCarriedOutToggle(
                              e.target.checked,
                              setWeeklyUnitIntake,
                            )
                          }
                        />
                        <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-sas-primary"></span>
                        <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></span>
                      </span>
                    </label>
                  </div>
                  <div className="p-4 flex flex-col gap-3 md:flex-row md:items-stretch">
                    <p className="text-sas-primary-dark text-sm md:text-base leading-relaxed md:flex-1 bg-sas-primary-hover/70 rounded-lg p-3">
                      It is recommended that men and women do not regularly
                      drink more than 14 units a week. You should spread your
                      drinking over three days or more if you drink as much as
                      14 units a week.
                    </p>
                    <div className="w-full md:w-56">
                      <label
                        htmlFor="weekly-unit-intake"
                        className="text-xs font-semibold uppercase tracking-[0.16em] text-sas-primary block mb-2"
                      >
                        Weekly Unit Intake
                      </label>
                      <input
                        id="weekly-unit-intake"
                        type="text"
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                        value={weeklyUnitIntake}
                        onChange={(e) => setWeeklyUnitIntake(e.target.value)}
                        disabled={isNotCarriedOutValue(weeklyUnitIntake)}
                      />
                    </div>
                  </div>
                </div>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://nhs.uk/live-well/alcohol-advice/the-risks-of-drinking-too-much"
                  className="inline-flex items-center mt-1"
                >
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="text-sas-primary"
                  />{" "}
                  <span className="text-sas-primary-dark pl-2 text-xs md:text-sm font-montserrat">
                    www.nhs.uk/live-well/exercise/exercise-guidelines
                  </span>
                </a>
              </div>

              <div className="relative my-8">
                <div className="rounded-2xl border border-sky-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-sky-100 bg-sky-50 flex items-center justify-between gap-3">
                    <h2 className="font-fatfrank text-lg md:text-xl text-sas-primary">
                      Physical Activity
                    </h2>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sas-primary">
                        Not carried out
                      </span>
                      <span className="relative inline-flex h-5 w-10 items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={isNotCarriedOutValue(weeklyHours)}
                          onChange={(e) =>
                            handleNotCarriedOutToggle(
                              e.target.checked,
                              setWeeklyHours,
                            )
                          }
                        />
                        <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-sas-primary"></span>
                        <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></span>
                      </span>
                    </label>
                  </div>
                  <div className="p-4 flex flex-col gap-3 md:flex-row md:items-stretch">
                    <p className="text-sas-primary-dark text-sm md:text-base leading-relaxed md:flex-1 bg-sas-primary-hover/70 rounded-lg p-3">
                      Adults should do some type of physical activity every day.
                      Adults should aim to do strength exercises and moderate
                      intensity activity spread over 4 to 5 days. You should
                      also limit the time you spend lying or sitting down.
                    </p>
                    <div className="w-full md:w-56">
                      <label
                        htmlFor="weekly-hours"
                        className="text-xs font-semibold uppercase tracking-[0.16em] text-sas-primary block mb-2"
                      >
                        Weekly Hours
                      </label>
                      <input
                        id="weekly-hours"
                        type="text"
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                        value={weeklyHours}
                        onChange={(e) => setWeeklyHours(e.target.value)}
                        disabled={isNotCarriedOutValue(weeklyHours)}
                      />
                    </div>
                  </div>
                </div>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://nhs.uk/live-well/exercise/exercise-guidelines"
                  className="inline-flex items-center mt-1"
                >
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="text-sas-primary"
                  />{" "}
                  <span className="text-sas-primary-dark pl-2 text-xs md:text-sm font-montserrat">
                    www.nhs.uk/live-well/alcohol-advice/the-risks-of-drinking-too-much
                  </span>
                </a>
              </div>
              <SmokerStatusSection
                smokerStatus={smokerStatus}
                onSmokerStatusChange={setSmokerStatus}
              />
            </div>
            <div className="relative z-10 px-4 mt-8">
              <FormFooterSection
                services={services}
                routineCancerChecks={routineCancerChecks}
                onRoutineCancerChecksChange={handleRoutineCancerChecksChange}
                recommendedServices={recommendedServices}
                onRecommendedServicesChange={handleRecommendedServicesChange}
                additionalNotes={additionalNotes}
                onAdditionalNotesChange={(e) =>
                  setAdditionalNotes(e.target.value)
                }
                optOut={optOut}
                onOptOutChange={(e) => setOptOut(e.target.checked)}
                submitLabel={submitLabel}
              />
            </div>
            {/* <div className="absolute bottom-0 z-0">
              <img src={wave} alt="base wave" className="overflow-hidden" />
            </div> */}
          </div>
        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default App;
